import {
  CardContent,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import StyledTableRow from "../../components/StyledTableRow";
import {
  getUniqueValues,
  countOrACVFormatter,
  dateFormatterMomentV2,
} from "../../util/customFunctions";
import tableStyle from "../../CustomerSuccess/styles/tableStyle";
import CopyTablesObject from "../../components/xlsx-export/CopyTablesObject";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";

const useResizeStyles = makeStyles(() => ({
  resizeHandle: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 5,
    cursor: "col-resize",
    "&:hover": {
      background: "rgba(255,255,255,0.3)",
    },
  },
}));

export default function RampAnalysisTable(props) {
  const classes = tableStyle();
  const resizeClasses = useResizeStyles();
  const tableContainerRef = useRef();
  const tableRef = React.createRef();

  const [rowPin, setrowPin] = useState(false);
  const [parentWidth, setparentWidth] = useState();
  const [sortField, setSortField] = useState("Hire_Date");
  const [sortDirection, setSortDirection] = useState("asc");
  const [colWidths, setColWidths] = useState({});
  const resizing = useRef(null);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    const handleScroll = () => {
      setrowPin(tableContainer.scrollTop > 0);
    };
    tableContainer.addEventListener("scroll", handleScroll);
    return () => tableContainer.removeEventListener("scroll", handleScroll);
  }, [tableContainerRef.current]);

  const updateParentWidth = () => {
    const newParentWidth =
      tableContainerRef.current.getBoundingClientRect().width;
    setparentWidth(newParentWidth);
  };

  useEffect(() => {
    updateParentWidth();
    window.addEventListener("resize", updateParentWidth);
    return () => window.removeEventListener("resize", updateParentWidth);
  }, []);

  // Column resize handlers
  const onMouseDown = useCallback((e, colKey) => {
    e.preventDefault();
    const startX = e.clientX;
    const th = e.target.closest("th");
    const startWidth = th ? th.getBoundingClientRect().width : 120;

    const onMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      setColWidths((prev) => ({
        ...prev,
        [colKey]: Math.max(30, startWidth + diff),
      }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ArrowUpwardIcon style={{ fontSize: 14, marginLeft: 2, verticalAlign: "middle" }} />
    ) : (
      <ArrowDownwardIcon style={{ fontSize: 14, marginLeft: 2, verticalAlign: "middle" }} />
    );
  };

  // Sort data
  const sortedData = useMemo(() => {
    const sorted = [...props.data];
    sorted.sort((a, b) => {
      let aVal, bVal;
      if (sortField === "rep") {
        aVal = (a.rep || "").toLowerCase();
        bVal = (b.rep || "").toLowerCase();
      } else if (sortField === "Hire_Date") {
        aVal = a.Hire_Date ? new Date(a.Hire_Date).getTime() : 0;
        bVal = b.Hire_Date ? new Date(b.Hire_Date).getTime() : 0;
      } else if (sortField === "Term_Date") {
        aVal = a.Term_Date ? new Date(a.Term_Date).getTime() : Infinity;
        bVal = b.Term_Date ? new Date(b.Term_Date).getTime() : Infinity;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [props.data, sortField, sortDirection]);

  const reps = sortedData.map((d) => d.rep).filter(getUniqueValues);
  const timeframe = props.timeFrame;

  const ast = (x) => (
    <>
      <span>{x}</span> <span style={{ color: "#ed7d31" }}>*</span>
    </>
  );

  const sortableHeaderStyle = {
    cursor: "pointer",
    userSelect: "none",
    position: "relative",
  };

  const renderResizeHandle = (colKey) => (
    <div
      className={resizeClasses.resizeHandle}
      onMouseDown={(e) => onMouseDown(e, colKey)}
    />
  );

  return (
    <Grid xs={12} justify="center">
      <Grid xs={12}>
        {parentWidth !== undefined ? (
          <div
            style={{
              display: "flex",
              justifyContent: "right",
              position: "relative",
              marginLeft: "1.5rem",
              width: `${parentWidth}px`,
            }}
          >
            <CopyTablesObject tableRef={tableRef} />
          </div>
        ) : null}
      </Grid>
      <TableContainer
        style={{
          margin: "2rem",
          marginBottom: "1rem",
          width: "auto",
          maxHeight: "85vh",
        }}
        id="myTableContainer"
        ref={tableContainerRef}
      >
        <Table
          ref={tableRef}
          size="small"
          aria-label="html table"
          style={{ tableLayout: "fixed" }}
          data-test={props.dataTest}
        >
          <TableHead style={{ position: "sticky", top: -1, zIndex: 5 }}>
            <TableRow
              style={{
                fontWeight: 600,
                background: "#4472c4",
                color: "white",
                paddingLeft: 5,
                height: 60,
              }}
            >
              <TableCell
                align="center"
                style={{
                  background: "#4472c4",
                  border: "0.5px solid lightgrey",
                  maxWidth: 50,
                  ...(colWidths["#"] ? { width: colWidths["#"] } : {}),
                  position: "relative",
                }}
                className={classes.colHeader}
              >
                #
                {renderResizeHandle("#")}
              </TableCell>
              <TableCell
                align="center"
                style={{
                  background: "#4472c4",
                  border: "0.5px solid lightgrey",
                  width: colWidths["rep"] || 120,
                  ...sortableHeaderStyle,
                }}
                className={classes.colHeader}
                onClick={() => handleSort("rep")}
              >
                Rep{sortIcon("rep")}
                {renderResizeHandle("rep")}
              </TableCell>
              <TableCell
                align="center"
                style={{
                  background: "#4472c4",
                  border: "0.5px solid lightgrey",
                  width: colWidths["Hire_Date"] || 120,
                  ...sortableHeaderStyle,
                }}
                className={classes.colHeader}
                onClick={() => handleSort("Hire_Date")}
              >
                Start Date{sortIcon("Hire_Date")}
                {renderResizeHandle("Hire_Date")}
              </TableCell>
              <TableCell
                align="center"
                style={{
                  background: "#4472c4",
                  border: "0.5px solid lightgrey",
                  width: colWidths["Term_Date"] || 120,
                  ...sortableHeaderStyle,
                }}
                className={classes.colHeader}
                onClick={() => handleSort("Term_Date")}
              >
                End Date{sortIcon("Term_Date")}
                {renderResizeHandle("Term_Date")}
              </TableCell>
              <TableCell
                align="center"
                style={{
                  background: "#4472c4",
                  border: "0.5px solid lightgrey",
                  width: colWidths["avgQ4"] || 120,
                  position: "relative",
                }}
                className={classes.colHeader}
              >{`Average of Hire Fiscal Qtr (HFQ) +4 onwards`}
                {renderResizeHandle("avgQ4")}
              </TableCell>
              {timeframe.map((t, index) => (
                <TableCell
                  key={t}
                  align="center"
                  style={{
                    ...(index % 2 === 0
                      ? {
                          background: "#5b9bd5",
                          color: "white",
                          outline: "0.4px solid lightgrey",
                          outlineOffset: "-1.5px",
                        }
                      : {
                          background: "#4471c4",
                          color: "white",
                          outline: "0.4px solid lightgrey",
                          outlineOffset: "-1.5px",
                        }),
                    width: colWidths[t] || 71,
                    position: "relative",
                  }}
                  className={classes.colHeader}
                >{`${t}`}
                  {renderResizeHandle(t)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.groupby === "Team"
              ? props.uniqueTeams.map((team) => {
                  let teamData = sortedData.filter(
                    (s) => s[props.groupbyColumn] === team
                  );
                  const repsWithTeam = teamData
                    .map((c) => c.rep)
                    .filter(getUniqueValues);
                  const qData = teamData;
                  const quotaReps = qData
                    .map((c) => c.avgQ4)
                    .filter(Boolean).length;
                  let average = teamData
                    .map((t) => t.avgQ4)
                    .reduce((a, b) => a + b, 0);
                  average =
                    ["", undefined, null, 0].includes(average) || quotaReps <= 0
                      ? "-"
                      : countOrACVFormatter(average / quotaReps / 1000, true);
                  let wondata = teamData.map((c) => c.wonData).flat();
                  return (
                    <React.Fragment key={team}>
                      <StyledTableRow>
                        <TableCell
                          align="left"
                          style={{
                            border: "0.5px solid lightgrey",
                            paddingRight: "5px",
                            fontWeight: 600,
                          }}
                          className={classes.datacell}
                        ></TableCell>
                        <TableCell
                          align="left"
                          style={{
                            border: "0.5px solid lightgrey",
                            paddingRight: "5px",
                            fontWeight: 600,
                          }}
                          className={classes.datacell}
                        >
                          {team}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            border: "0.5px solid lightgrey",
                            paddingRight: "5px",
                            fontWeight: 600,
                          }}
                          className={classes.datacell}
                        >
                          {"-"}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            border: "0.5px solid lightgrey",
                            paddingRight: "5px",
                            fontWeight: 600,
                          }}
                          className={classes.datacell}
                        >
                          {"-"}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            border: "0.5px solid lightgrey",
                            fontWeight: 600,
                          }}
                          className={classes.datacell}
                        >
                          {average}
                        </TableCell>
                        {timeframe.map((t) => {
                          const timeFrameData = wondata.filter(
                            (c) => c.type === t
                          );
                          const quotaData = timeFrameData;
                          const timeAcv =
                            timeFrameData.length > 0
                              ? timeFrameData
                                  .map((c) => c.acv)
                                  .filter(Boolean)
                                  .reduce((a, b) => a + b, 0)
                              : 0;
                          const noOfReps =
                            timeFrameData.length > 0
                              ? quotaData
                                  .map((c) => ({ acv: c.acv, quota: c.quota }))
                                  .filter((f) => f.acv >= 0 || f.quota >= 0)
                                  .length
                              : 0;
                          const acv = ["", undefined, null].includes(timeAcv)
                            ? "-"
                            : countOrACVFormatter(
                                noOfReps > 0 ? timeAcv / 1000 / noOfReps : 0,
                                true
                              );
                          return (
                            <TableCell
                              key={t}
                              align="right"
                              style={{
                                border: "0.5px solid lightgrey",
                                fontWeight: 600,
                              }}
                              className={classes.datacell}
                            >
                              {acv}
                            </TableCell>
                          );
                        })}
                      </StyledTableRow>
                      {repsWithTeam.map((rep, id) => {
                        let repData = teamData.filter(
                          (s) => s.rep === rep
                        )[0];
                        let startDate = ["", undefined, null].includes(
                          repData.Hire_Date
                        )
                          ? "-"
                          : dateFormatterMomentV2(repData.Hire_Date);
                        let endDate = ["", undefined, null].includes(
                          repData.Term_Date
                        )
                          ? "-"
                          : dateFormatterMomentV2(repData.Term_Date);
                        let average = ["", undefined, null].includes(
                          repData.avgQ4
                        )
                          ? "-"
                          : countOrACVFormatter(repData.avgQ4 / 1000, true);
                        let wondata = repData.wonData;
                        let termDateFQ = ["", undefined, null].includes(
                          repData.maxTermHFQ
                        )
                          ? "-"
                          : repData.maxTermHFQ;
                        const bgColor = endDate === "-" ? "gray" : "#282727";
                        return (
                          <StyledTableRow key={rep}>
                            <TableCell
                              align="left"
                              style={{
                                border: "0.5px solid lightgrey",
                                paddingRight: "5px",
                              }}
                              className={classes.datacell}
                            >
                              {id + 1}
                            </TableCell>
                            <TableCell
                              align="left"
                              style={{
                                border: "0.5px solid lightgrey",
                                paddingRight: "5px",
                              }}
                              className={`${classes.datacell} ${classes.indentName}`}
                            >
                              {endDate !== "-" ? ast(rep) : rep}
                            </TableCell>
                            <TableCell
                              align="right"
                              style={{
                                border: "0.5px solid lightgrey",
                                paddingRight: "5px",
                              }}
                              className={classes.datacell}
                            >
                              {startDate}
                            </TableCell>
                            <TableCell
                              align="right"
                              style={{
                                border: "0.5px solid lightgrey",
                                paddingRight: "5px",
                              }}
                              className={classes.datacell}
                            >
                              {endDate}
                            </TableCell>
                            <TableCell
                              align="right"
                              style={{ border: "0.5px solid lightgrey" }}
                              className={classes.datacell}
                            >
                              {average}
                            </TableCell>
                            {timeframe.map((t) => {
                              const timeFrameData = wondata.filter(
                                (c) => c.type === t
                              );
                              const Quota =
                                timeFrameData.length > 0
                                  ? timeFrameData[0].quota
                                  : null;
                              let timeAcv =
                                timeFrameData.length > 0
                                  ? timeFrameData[0].acv
                                  : parseInt(t.split("-")[1]) <=
                                    parseInt(termDateFQ.split("-")[1])
                                  ? 0
                                  : null;
                              timeAcv =
                                [undefined].includes(timeAcv) && Quota >= 0
                                  ? 0
                                  : timeAcv;
                              const acv = [undefined].includes(timeAcv, Quota)
                                ? "-"
                                : ["", null].includes(timeAcv)
                                ? ""
                                : countOrACVFormatter(timeAcv / 1000, true);
                              let timeCount =
                                timeFrameData.length > 0
                                  ? timeFrameData[0].count
                                  : null;
                              let count = [undefined].includes(timeCount)
                                ? "-"
                                : ["", null].includes(timeCount)
                                ? ""
                                : timeCount;
                              let cfq =
                                timeFrameData.length > 0
                                  ? timeFrameData[0].closed_fiscal_quarter
                                  : "-";
                              return (
                                <TableCell
                                  key={t}
                                  align="right"
                                  style={
                                    count === ""
                                      ? { backgroundColor: bgColor }
                                      : {
                                          border: "0.5px solid lightgrey",
                                          cursor: "pointer",
                                        }
                                  }
                                  onClick={() =>
                                    count !== "" &&
                                    props.handleChangeLocation(
                                      "drilldownOnGraph",
                                      rep,
                                      cfq,
                                      team
                                    )
                                  }
                                  className={classes.datacell}
                                >
                                  {count !== ""
                                    ? acv === ""
                                      ? "$0"
                                      : acv
                                    : acv}
                                </TableCell>
                              );
                            })}
                          </StyledTableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              : sortedData.map((repData, id) => {
                  const rep = repData.rep;
                  let startDate = ["", undefined, null].includes(
                    repData.Hire_Date
                  )
                    ? "-"
                    : dateFormatterMomentV2(repData.Hire_Date);
                  let endDate = ["", undefined, null].includes(
                    repData.Term_Date
                  )
                    ? "-"
                    : dateFormatterMomentV2(repData.Term_Date);
                  let average = ["", undefined, null].includes(repData.avgQ4)
                    ? "-"
                    : countOrACVFormatter(repData.avgQ4 / 1000, true);
                  let termDateFQ = ["", undefined, null].includes(
                    repData.maxTermHFQ
                  )
                    ? "-"
                    : repData.maxTermHFQ;
                  const bgColor = endDate === "-" ? "gray" : "#282727";
                  let wondata = repData.wonData;
                  return (
                    <StyledTableRow key={rep}>
                      <TableCell
                        align="left"
                        style={{
                          border: "0.5px solid lightgrey",
                          paddingRight: "5px",
                        }}
                        className={classes.datacell}
                      >
                        {id + 1}
                      </TableCell>
                      <TableCell
                        align="left"
                        style={{
                          border: "0.5px solid lightgrey",
                          paddingRight: "5px",
                        }}
                        className={classes.datacell}
                      >
                        {endDate !== "-" ? ast(rep) : rep}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          border: "0.5px solid lightgrey",
                          paddingRight: "5px",
                        }}
                        className={classes.datacell}
                      >
                        {startDate}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          border: "0.5px solid lightgrey",
                          paddingRight: "5px",
                        }}
                        className={classes.datacell}
                      >
                        {endDate}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{ border: "0.5px solid lightgrey" }}
                        className={classes.datacell}
                      >
                        {average}
                      </TableCell>
                      {timeframe.map((t) => {
                        const timeFrameData = wondata.filter(
                          (c) => c.type === t
                        );
                        const Quota =
                          timeFrameData.length > 0
                            ? timeFrameData[0].quota
                            : null;
                        let timeAcv =
                          timeFrameData.length > 0
                            ? timeFrameData[0].acv
                            : parseInt(t.split("-")[1]) <=
                              parseInt(termDateFQ.split("-")[1])
                            ? 0
                            : null;
                        timeAcv =
                          [undefined].includes(timeAcv) && Quota >= 0
                            ? 0
                            : timeAcv;
                        const acv = [undefined].includes(timeAcv, Quota)
                          ? "-"
                          : ["", null].includes(timeAcv)
                          ? ""
                          : countOrACVFormatter(timeAcv / 1000, true);
                        let timeCount =
                          timeFrameData.length > 0
                            ? timeFrameData[0].count
                            : null;
                        let count = [undefined].includes(timeCount)
                          ? "-"
                          : ["", null].includes(timeCount)
                          ? ""
                          : timeCount;
                        let cfq =
                          timeFrameData.length > 0
                            ? timeFrameData[0].closed_fiscal_quarter
                            : "-";
                        return (
                          <TableCell
                            key={t}
                            align="right"
                            style={
                              count === ""
                                ? { backgroundColor: bgColor }
                                : {
                                    border: "0.5px solid lightgrey",
                                    cursor: "pointer",
                                  }
                            }
                            onClick={() =>
                              count !== "" &&
                              props.handleChangeLocation(
                                "drilldownOnGraph",
                                rep,
                                cfq
                              )
                            }
                            className={classes.datacell}
                          >
                            {count !== "" ? (acv === "" ? "$0" : acv) : acv}
                          </TableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
}
